import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

/**
 * CategorySidebar
 * Desktop: vertical list with active state
 * Mobile: collapsed dropdown
 *
 * Props:
 *  - categories: [{slug, name, count}]
 *  - activeSlug: string | null
 *  - onPick: (slug: string | "all") => void     // optional (Destinations uses filter state)
 *  - totalPosts: number
 *  - mode: "filter" (uses onPick) | "link" (renders <Link to={/category/slug}>)
 *  - heading: string
 */
export default function CategorySidebar({
  categories = [],
  activeSlug = null,
  onPick,
  totalPosts = 0,
  mode = "filter",
  heading = "Categories",
  children = null,
}) {
  const [openMobile, setOpenMobile] = useState(false);
  const loc = useLocation();
  const activeLabel =
    activeSlug && activeSlug !== "all"
      ? categories.find((c) => c.slug === activeSlug)?.name
      : `All (${totalPosts})`;

  const renderItem = (slug, name, count, isActive, key) => {
    const content = (
      <div
        className={`flex items-baseline justify-between gap-3 py-2 px-3 -mx-3 rounded transition-all ${
          isActive
            ? "bg-[#9c4122]/8 text-[#9c4122]"
            : "text-stone-700 hover:bg-stone-100 hover:text-stone-900"
        }`}
      >
        <span
          className={`font-serif text-[17px] leading-snug ${
            isActive ? "font-medium" : ""
          }`}
        >
          {name}
        </span>
        <span className="text-[11px] tracking-widest tabular-nums text-stone-400">
          {count}
        </span>
      </div>
    );
    if (mode === "link") {
      return (
        <Link
          key={key}
          to={slug === "all" ? "/destinations" : `/category/${slug}`}
          data-testid={`sidebar-category-${slug}`}
          className="block"
        >
          {content}
        </Link>
      );
    }
    return (
      <button
        key={key}
        type="button"
        onClick={() => {
          onPick?.(slug);
          setOpenMobile(false);
        }}
        data-testid={`sidebar-category-${slug}`}
        className="block w-full text-left"
      >
        {content}
      </button>
    );
  };

  return (
    <>
      {/* Mobile: dropdown */}
      <div className="lg:hidden sticky top-[72px] z-30 bg-[#FDFBF7]/95 backdrop-blur-md border-b border-stone-200 -mx-6 px-6 py-3">
        <button
          type="button"
          onClick={() => setOpenMobile((v) => !v)}
          data-testid="sidebar-mobile-toggle"
          className="w-full flex items-center justify-between px-4 py-2.5 border border-stone-300 bg-white rounded-full"
        >
          <span className="text-xs tracking-[0.2em] uppercase text-stone-500">
            {heading}:
            <span className="ml-2 font-semibold text-stone-900 tracking-normal normal-case text-[13px]">
              {activeLabel}
            </span>
          </span>
          <ChevronDown
            size={16}
            className={`transition-transform ${openMobile ? "rotate-180" : ""}`}
          />
        </button>
        {openMobile && (
          <div
            data-testid="sidebar-mobile-panel"
            className="mt-3 rounded-lg border border-stone-200 bg-white max-h-80 overflow-y-auto p-2"
          >
            {mode === "filter" &&
              renderItem("all", "All stories", totalPosts, activeSlug === "all" || !activeSlug, "all")}
            {categories.map((c) =>
              renderItem(c.slug, c.name, c.count, activeSlug === c.slug, c.slug)
            )}
          </div>
        )}
      </div>

      {/* Desktop: sidebar */}
      <aside
        data-testid="category-sidebar"
        className="hidden lg:block"
      >
        <div className="sticky top-28">
          <p className="kicker text-stone-500 text-[10px] mb-4">{heading}</p>
          <nav className="space-y-0.5">
            {mode === "filter" &&
              renderItem("all", "All stories", totalPosts, activeSlug === "all" || !activeSlug, "all")}
            {categories.map((c) =>
              renderItem(c.slug, c.name, c.count, activeSlug === c.slug || loc.pathname === `/category/${c.slug}`, c.slug)
            )}
          </nav>
          {children && <div className="mt-10">{children}</div>}
        </div>
      </aside>
    </>
  );
}
